/// EVENTS ///
import { SyntheticEvent } from "react";
import { MouseEvent as ReactMouseEvent } from "react";
/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
import useDraw from "hooks/draw";
import { useEffect } from "react";
import useMousePosition from "hooks/mouse_position";
import { useState } from "react";
/// UUID ///
import { v4 } from "uuid";
/// HANDLERS ///
import { handleCreateCounter } from "handler/counter/create";
import { handleDeleteCounter } from "handler/counter/delete";
import { handleRotateCounter } from "handler/counter/rotate";
/// TYPES ///
import { Corner } from "api/types/sketch";
import { Counter } from "api/types/sketch";
import { HighlightData } from "types/draw";
import { Label } from "api/types/sketch";
import { MouseData } from "types/mouse_position";
import { Point } from "api/types/sketch";
import { Slab } from "api/types/quote";
import { Vector2 } from "api/types/sketch";
import { CutShape } from "api/types/sketch";
import { IHistoryActionType } from "api/types/history";
/// ACTIONS ///
import { updateCounter } from "api/actions/sketch/counter";
import { createLabel } from "api/actions/sketch/label";
import { deleteLabel } from "api/actions/sketch/label";
import { updateLabel } from "api/actions/sketch/label";
import { updateLocalLabel } from "api/actions/sketch/label";
import { createLocalCounter } from "api/actions/sketch/counter";
import { updateLocalCounter } from "api/actions/sketch/counter";
import { createBulkCutShapes } from "api/actions/sketch/cut_shape";
import { updateLocalCutShapes } from "api/actions/sketch/cut_shape";
import { createRestorePoint } from "api/actions/history";
import { markQuoteChanged } from "api/actions/quotes/quote";
/// FUNCTIONS ///
import { getDirection } from "functions/sketch";
import { getNextPoint } from "functions/sketch";
import { getBranchAngle } from "functions/sketch";
import { isPointInRectangle } from "functions/sketch/math";
import { getDistance } from "functions/sketch/math";
import { getDimensions } from "functions/sketch/math";
import { checkCounterClicked } from "functions/sketch/check";
import { getCutShapeByCounterUuid } from "functions/sketch";
import { getFirstSlabInAreas } from "functions/sketch/get/slab";
import { moveCounter } from "functions/sketch";
import { reverseCounter } from "functions/sketch";
import { checkIfCornerIsBeginning } from "functions/sketch/check";
import { duplicateCounter } from "functions/sketch";
import { getCounterByUuid } from "functions/sketch";
import { createCutShapes } from "functions/sketch";
import { getMouseAtZoom } from "functions/sketch";
import { moveVector } from "functions/sketch/move/vector";
import { createCorners } from "functions/sketch";
/// MUI ///
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
/// COMPONENTS ///
import StoreAreaBar from "../bar/area";
import CreateStoreArea from "../dialog/area/create";
import LabelMenu from "components/quote/menu/label";
import SketchMenu from "components/quote/menu/sketch";
import UpdateStoreArea from "../drawer/update_area";
import UpdateStoreHeight from "../drawer/update_height";
import UpdateStoreWidth from "../drawer/update_width";
import UpdateStoreDegrees from "../drawer/update_degrees";
import UpdateStoreLabel from "../dialog/label/update";
/// FUNCTIONS ///
import { checkIndicatorSelected } from "functions/sketch/check/indicator";
import { updateAngle } from "functions/sketch";
import { updateHeight } from "functions/sketch";
import { updateWidth } from "functions/sketch";
/// EMPTY VALUES ///
import { empty_counter } from "values/empty/quote/counter";
import { empty_point } from "values/empty/quote/point";
/// STYLES ///
import { sketch_style } from "styles/sketch";
import { canvasStyle } from "styles/sketch";
/// TEXT VALUES ///
import { HEIGHT } from "values/text";
import { WIDTH } from "values/text";
import { ANGLE } from "values/text";
/// VALUES ///
import { getCopyMessage } from "values/text";

interface Props {
  index: number,
  zoom: number,
  setDisabled: (disabled: boolean) => void,
  current_area: number,
  setCurrentArea: (area: number) => void,
  copying: boolean,
  setCopying: (copying: boolean) => void
}

export default function StoreMeasurements({ index, zoom, setDisabled, current_area, setCurrentArea, copying, setCopying }: Props) {
  const dispatch = useAppDispatch();
  const [selected_counter, setSelectedCounter] = useState<Counter | null>(null);
  const mouse_data: MouseData = useMousePosition();
  const ref = mouse_data.ref;
  const { counters } = useAppSelector((state) => state.sketch);
  const { cut_shapes } = useAppSelector((state) => state.sketch);
  const { labels } = useAppSelector((state) => state.sketch);
  const { quote } = useAppSelector((state) => state.quote);
  const { areas } = useAppSelector((state) => state.quote);
  const [lifting_label, setLiftingLabel] = useState<boolean>(false);
  const [selected_label, setSelectedLabel] = useState<Label | null>(null);
  const [in_operation, setInOperation] = useState<boolean>(false);
  const [drawing, setDrawing] = useState<boolean>(false);
  const [lifting, setLifting] = useState<boolean>(false);
  const [lift_point, setLiftPoint] = useState<Vector2>({ X: 0, Y: 0 });
  const [menu_location, setMenuLocation] = useState<Vector2>({ X: 0, Y: 0 });
  const [menu_open, setMenuOpen] = useState<boolean>(false);
  const [label_menu_open, setLabelMenuOpen] = useState<boolean>(false);
  const [edit_label_open, setEditLabelOpen] = useState<boolean>(false);
  const [height_open, setHeightOpen] = useState<boolean>(false);
  const [width_open, setWidthOpen] = useState<boolean>(false);
  const [degrees_open, setDegreesOpen] = useState<boolean>(false);
  const [area_open, setAreaOpen] = useState<boolean>(false);
  const [redrawing, setRedrawing] = useState<boolean>(false);
  const [create_area_open, setCreateAreaOpen] = useState<boolean>(false);
  const [current_uuid, setCurrentUuid] = useState<string>("");
  const [original_value, setOriginalValue] = useState<number | null>(null);
  const [copy, setCopy] = useState<HighlightData | null>(null);
  const [original_counter, setOriginalCounter] = useState<Counter>({});

  const highlight_data: HighlightData = useDraw(
    index,
    selected_counter,
    areas[current_area]?.uuid,
    mouse_data,
    height_open || width_open || degrees_open || area_open || create_area_open || create_area_open || label_menu_open || edit_label_open,
    drawing,
    lifting,
    in_operation,
    zoom,
    {
      show_measurements: true,
      ends_selectable: true,
      show_degrees: true,
      text_selectable: !drawing && !height_open && !width_open && !degrees_open && !area_open && !lifting && !menu_open && !create_area_open && !label_menu_open && !edit_label_open
    }
  );

  const handleEvent = (event: SyntheticEvent) => {
    event.preventDefault();

    const mouse_event: MouseEvent | null = event.nativeEvent instanceof MouseEvent ? event.nativeEvent as MouseEvent : null;

    const mouse: Vector2 = mouse_data.position;
    if ((mouse_event?.type === "mouseup" && mouse_event?.button === 0)) {
      if (copying) {
        setInOperation(false);
        return;
      }
      if (drawing) {
        if (selected_counter && quote?.id && redrawing) {
          const counter: Counter = { ...selected_counter };
          const cut_shape: CutShape | null = getCutShapeByCounterUuid(cut_shapes, counter.uuid);
          const generated_cut_shapes: CutShape[] = createCutShapes(
            counter,
            cut_shape?.area_uuid ?? areas[0]?.uuid,
            cut_shape?.slab_uuid ?? areas[0]?.slabs[0]?.uuid,
            counter?.points,
            counter?.corners,
            []
          );
          dispatch(updateCounter(counter));
          dispatch(updateLocalCutShapes(generated_cut_shapes, counter.uuid));
          dispatch(createBulkCutShapes(generated_cut_shapes));
          dispatch(createRestorePoint(
            {
              undo_action: IHistoryActionType.REDRAW_COUNTER,
              redo_action: IHistoryActionType.REDRAW_COUNTER,
              desc: "Redraw Counter",
              undo_data: original_counter,
              redo_data: counter
            }
          ));
          dispatch(markQuoteChanged(quote?.id));
        }
        else {
          if (
            quote &&
            areas?.length > 0 &&
            selected_counter?.uuid &&
            selected_counter?.points?.length > 1 &&
            selected_counter?.corners?.length > 3
          ) {
            const counter: Counter = { ...selected_counter, drawing_color: current_area };
            handleCreateCounter(
              dispatch,
              quote.id,
              counter,
              areas[current_area]?.uuid ?? areas[0]?.uuid ?? "could not find an area",
              areas[current_area]?.slabs[0]?.uuid ?? areas[0]?.uuid ?? "could not find a slab"
            );
          }
          setSelectedCounter(null);
        }
        setDrawing(false);
        setDisabled(false);
      }
      else if (lifting) {
        if (selected_counter) {
          const counter: Counter | null = getCounterByUuid(selected_counter.uuid, counters);
          if (counter) {
            dispatch(updateCounter(counter));
            dispatch(createRestorePoint(
              {
                undo_action: IHistoryActionType.MOVE_COUNTER,
                redo_action: IHistoryActionType.MOVE_COUNTER,
                desc: "Move Counter",
                undo_data: { ...selected_counter },
                redo_data: counter
              }
            ));
          }
        }
        setLifting(false);
      }
      else if (lifting_label && selected_label) {
        const label: Label = { ...selected_label };
        const vector: Vector2 = moveVector(selected_label.location, getMouseAtZoom(mouse, zoom));
        dispatch(updateLabel({ ...label, location: vector }, label.id));
        dispatch(updateLocalLabel({ ...label, location: vector }));
        setSelectedLabel(null);
        setLiftingLabel(false);
      }
      setInOperation(false);
      return;
    }
    if (in_operation) {
      return;
    }

    setInOperation(true);
    if ((mouse_event?.type === "mousedown" && mouse_event?.button === 0)) {
      if (copying && !copy) {
        if (highlight_data?.text) {
          if (
            highlight_data?.text?.type === WIDTH ||
            highlight_data?.text?.type === HEIGHT ||
            highlight_data?.text?.type === ANGLE
          ) {
            setCopy(highlight_data);
          }
        }
        setInOperation(false);
        return;
      }
      else if (copying && copy) {
        if (highlight_data?.text?.type === WIDTH && (copy?.text?.type === WIDTH || copy?.text?.type === HEIGHT)) {
          const original_counter: Counter | null = getCounterByUuid(highlight_data?.text?.counter_uuid, counters);
          const target_counter: Counter | null = updateWidth(original_counter, highlight_data?.text?.point_uuid, copy?.text?.value);
          const new_corners: Corner[] = createCorners(target_counter.points, [...target_counter.corners], target_counter.uuid);
          if (target_counter) {
            const new_counter: Counter = { ...target_counter, corners: new_corners };
            const original_cut_shapes = [...cut_shapes.filter(shape => shape.counter_uuid === new_counter.uuid)];
            dispatch(updateLocalCounter(new_counter));
            dispatch(updateCounter(new_counter));

            const cut_shape: CutShape | null = getCutShapeByCounterUuid(cut_shapes, new_counter.uuid);
            const generated_cut_shapes: CutShape[] = createCutShapes(
              new_counter,
              cut_shape?.area_uuid ?? areas[0]?.uuid,
              cut_shape?.slab_uuid ?? areas[0]?.slabs[0]?.uuid,
              new_counter?.points,
              new_corners,
              []
            );
            dispatch(updateLocalCutShapes(generated_cut_shapes, new_counter.uuid));
            dispatch(createBulkCutShapes(generated_cut_shapes));
            dispatch(createRestorePoint(
              {
                undo_action: IHistoryActionType.UPDATE_COUNTER,
                redo_action: IHistoryActionType.UPDATE_COUNTER,
                desc: "Copy Measurement",
                undo_data: original_counter,
                undo_data2: original_cut_shapes,
                redo_data: new_counter,
                redo_data2: generated_cut_shapes
              }
            ));
            dispatch(markQuoteChanged(quote?.id));
          }
        }
        else if (highlight_data?.text?.type === HEIGHT && (copy?.text?.type === HEIGHT || copy?.text?.type === WIDTH)) {
          const original_counter: Counter | null = getCounterByUuid(highlight_data?.text?.counter_uuid, counters);
          const target_counter: Counter | null = updateHeight(original_counter, copy?.text?.value, highlight_data?.text?.value, highlight_data?.text?.point_uuid);
          const new_corners: Corner[] = createCorners(target_counter.points, [...target_counter.corners], target_counter.uuid);
          if (target_counter) {
            const new_counter: Counter = { ...target_counter, corners: new_corners };
            const original_cut_shapes = [...cut_shapes.filter(shape => shape.counter_uuid === new_counter.uuid)];

            dispatch(updateLocalCounter(new_counter));
            dispatch(updateCounter(new_counter));

            const cut_shape: CutShape | null = getCutShapeByCounterUuid(cut_shapes, new_counter.uuid);
            const generated_cut_shapes: CutShape[] = createCutShapes(
              new_counter,
              cut_shape?.area_uuid ?? areas[0]?.uuid,
              cut_shape?.slab_uuid ?? areas[0]?.slabs[0]?.uuid,
              new_counter?.points,
              new_corners,
              []
            );
            dispatch(updateLocalCutShapes(generated_cut_shapes, new_counter.uuid));
            dispatch(createBulkCutShapes(generated_cut_shapes));
            dispatch(createRestorePoint(
              {
                undo_action: IHistoryActionType.UPDATE_COUNTER,
                redo_action: IHistoryActionType.UPDATE_COUNTER,
                desc: "Copy Measurement",
                undo_data: original_counter,
                undo_data2: original_cut_shapes,
                redo_data: new_counter,
                redo_data2: generated_cut_shapes
              }
            ));
            dispatch(markQuoteChanged(quote?.id));
          }
        }
        else if (highlight_data?.text?.type === ANGLE && copy?.text?.type === ANGLE) {
          const counter: Counter | null = getCounterByUuid(highlight_data?.text?.counter_uuid, counters);
          const update_counter: Counter | null = updateAngle(counter, highlight_data?.text?.point_uuid, copy?.text?.value);
          if (update_counter) {
            dispatch(updateLocalCounter(update_counter));
            dispatch(updateCounter(update_counter));

            const cut_shape: CutShape | null = getCutShapeByCounterUuid(cut_shapes, update_counter.uuid);
            const generated_cut_shapes: CutShape[] = createCutShapes(
              update_counter,
              cut_shape?.area_uuid ?? areas[0]?.uuid,
              cut_shape?.slab_uuid ?? areas[0]?.slabs[0]?.uuid,
              update_counter?.points,
              update_counter?.corners,
              []
            );
            dispatch(updateLocalCutShapes(generated_cut_shapes, counter.uuid));
            dispatch(createBulkCutShapes(generated_cut_shapes));
            dispatch(createRestorePoint(
              {
                undo_action: IHistoryActionType.UPDATE_COUNTER,
                redo_action: IHistoryActionType.UPDATE_COUNTER,
                desc: "Copy Angle",
                undo_data: counter,
                undo_data2: [...cut_shapes.filter(shape => shape.counter_uuid === counter.uuid)],
                redo_data: update_counter,
                redo_data2: generated_cut_shapes
              }
            ));
            dispatch(markQuoteChanged(quote?.id));
          }
        }
        setInOperation(false);
        return;
      }
      if (!height_open && !width_open && !degrees_open && !area_open && !create_area_open && !menu_open && !lifting && !drawing && !label_menu_open && !edit_label_open) {
        for (let i = 0; i < labels.length; i++) {
          if (checkIndicatorSelected(labels[i].location, getMouseAtZoom(mouse, zoom), true, 55)) {
            setSelectedLabel(labels[i]);
            setLiftingLabel(true);
            return;
          }
        }

        if (highlight_data && highlight_data.corner) {
          setDisabled(true);
          const counter: Counter | null = getCounterByUuid(highlight_data.corner.counter_uuid, counters);
          setOriginalCounter({ ...counter });
          if (counter) {
            if (checkIfCornerIsBeginning(counter, highlight_data.corner)) {
              const reversed_counter: Counter = reverseCounter(counter);
              setSelectedCounter(reversed_counter);
              dispatch(updateLocalCounter(reversed_counter));
            }
            else {
              setSelectedCounter(counter);
            }
          }
          setRedrawing(true);
          setDrawing(true);
          setInOperation(false);
          return;
        }

        if (highlight_data && highlight_data.text) {
          if (highlight_data.text.type === WIDTH) {
            setWidthOpen(true);
            const counter: Counter | null = getCounterByUuid(highlight_data.text.counter_uuid, counters);
            setSelectedCounter(counter);
            setCurrentUuid(highlight_data.text.point_uuid);
            setOriginalValue(highlight_data.text.value);
            setInOperation(false);
            return;
          }
          else if (highlight_data.text.type === HEIGHT) {
            setHeightOpen(true);
            const counter: Counter | null = getCounterByUuid(highlight_data.text.counter_uuid, counters);
            setSelectedCounter(counter);
            setCurrentUuid(highlight_data.text.point_uuid);
            setOriginalValue(highlight_data.text.value)
            setInOperation(false);
            return;
          }
          else if (highlight_data.text.type === ANGLE) {
            setDegreesOpen(true);
            const counter: Counter | null = getCounterByUuid(highlight_data.text.counter_uuid, counters);
            setSelectedCounter(counter);
            setCurrentUuid(highlight_data.text.point_uuid);
            setOriginalValue(highlight_data.text.value);
            setInOperation(false);
            return;
          }
        }
        let lifting: boolean = false;

        const counter: Counter | null = checkCounterClicked(counters.filter(c => c.area_uuid === areas[current_area].uuid), getMouseAtZoom(mouse, zoom));
        if (counter && !in_operation) {
          setLiftPoint(mouse);
          setSelectedCounter(counter);
          lifting = true;
          setLifting(true);
        }
        if (!lifting && quote) {
          setRedrawing(false);
          const counter_uuid: string = v4();
          const new_counter: Counter = {
            ...empty_counter,
            quote: quote.id,
            uuid: counter_uuid,
            drawing_color: current_area ?? 0,
            area: areas[current_area]?.id ?? areas[0]?.id ?? 0,
            area_uuid: areas[current_area]?.uuid ?? "area not detected",
            points: [{ ...empty_point, location: getMouseAtZoom(mouse, zoom), uuid: v4() }]
          }
          dispatch(createLocalCounter(new_counter));
          setSelectedCounter(new_counter);
          setDrawing(true);
          setDisabled(true);
        }
      }
      else {
        const target: HTMLDivElement = event.target as HTMLDivElement;
        if (!target.id.startsWith("menu")) {
          setMenuOpen(false);
          setLabelMenuOpen(false);
        }
      }
    }
    else if ((mouse_event?.type === "mousedown" && mouse_event?.button === 2)) {
      if (copying) {
        setCopying(false);
        setCopy(null);
        setInOperation(false);
        return;
      }
      for (let i = 0; i < labels.length; i++) {
        if (checkIndicatorSelected(labels[i].location, getMouseAtZoom(mouse, zoom), true)) {
          setSelectedLabel(labels[i]);
          setMenuLocation({
            X: mouse_event.pageX,
            Y: mouse_event.pageY
          });
          setLabelMenuOpen(true);
          setInOperation(false);
          return;
        }
      }

      const counter: Counter | null = checkCounterClicked(counters.filter(c => c.area_uuid === areas[current_area].uuid), getMouseAtZoom(mouse, zoom));

      if (counter && !in_operation) {
        setSelectedCounter(counter);
        setMenuLocation({
          X: mouse_event.pageX,
          Y: mouse_event.pageY
        });
        setMenuOpen(true);
        setInOperation(false);
        return;
      }
    }
    setInOperation(false);
  }

  useEffect(() => {
    const mouse: Vector2 = getMouseAtZoom(mouse_data.position, zoom);

    if (drawing && selected_counter !== null) {
      const counter: Counter = { ...selected_counter };
      const num_points: number = counter.points.length;
      const position: number = num_points - 1;

      if (num_points === 1) {
        const dimensions: Vector2 = getDimensions(counter.points[position].location, mouse);
        const angle: number = getDirection(dimensions);
        const next_location: Vector2 = getNextPoint(counter.points[position].location, angle, dimensions);
        const next_point: Point = { ...empty_point, uuid: v4(), location: next_location, angle: angle, position: position + 1 };
        const points: Point[] = [...counter.points, next_point];
        const corners: Corner[] = createCorners(points, selected_counter.corners, selected_counter.uuid);
        const new_counter: Counter = { ...counter, corners, points };
        dispatch(updateLocalCounter(new_counter));
        setSelectedCounter(new_counter);
        return;
      }
      else if (num_points === 2) {
        const last_point: Point = { ...counter.points[position - 1] };
        const current_point: Point = { ...counter.points[position] };
        const dimensions: Vector2 = getDimensions(last_point.location, mouse);
        const angle: number = getDirection(dimensions);
        last_point.angle = angle;
        const new_location: Vector2 = getNextPoint(last_point.location, angle, dimensions);
        current_point.location = new_location;
        let points: Point[] = [last_point, current_point];
        const distance = getDistance(last_point.location, current_point.location);
        const branch_angle: number | null = getBranchAngle(current_point.location, angle, last_point.width, mouse);
        if (distance > 150 && branch_angle !== null) {
          const next_location: Vector2 = getNextPoint(points[position].location, branch_angle, dimensions);
          points = [...points.slice(0, -1),
          { ...current_point, angle: branch_angle },
          {
            ...empty_point,
            location: next_location,
            uuid: v4(),
            angle: branch_angle,
            width: last_point.width,
            position: position + 1
          }
          ];
        }
        const corners: Corner[] = createCorners(points, selected_counter.corners, selected_counter.uuid);
        const new_counter: Counter = { ...counter, points, corners };
        dispatch(updateLocalCounter(new_counter));
        setSelectedCounter(new_counter);
        return;
      }
      else if (num_points > 2) {
        const last_point: Point = { ...counter.points[position - 1] };
        const current_point: Point = { ...counter.points[position] };
        const dimensions: Vector2 = getDimensions(last_point.location, mouse);

        const new_location: Vector2 = getNextPoint(last_point.location, last_point.angle, dimensions);
        current_point.location = new_location;

        let points: Point[] = [...counter.points.slice(0, -2), last_point, current_point];

        if (isPointInRectangle(last_point.location, last_point.angle, last_point.width, last_point.width, current_point.location)) {
          points.pop();
          const corners: Corner[] = createCorners(points, selected_counter.corners, selected_counter.uuid);
          const new_counter: Counter = { ...counter, points, corners };
          dispatch(updateLocalCounter(new_counter));
          setSelectedCounter(new_counter);
          return;
        }

        const distance = getDistance(last_point.location, current_point.location);
        const branch_angle: number | null = getBranchAngle(current_point.location, last_point.angle, last_point.width, mouse);

        if (distance > 150 && branch_angle !== null && (
          branch_angle === last_point.angle + 90 ||
          branch_angle === last_point.angle - 90 ||
          (branch_angle + last_point.angle + 90) % 360 === 0 ||
          (branch_angle + last_point.angle - 90) % 360 === 0
        )) {
          const next_location: Vector2 = getNextPoint(points[position].location, branch_angle, dimensions);
          points = [...points.slice(0, -1),
          { ...current_point, angle: branch_angle },
          {
            ...empty_point,
            location: next_location,
            uuid: v4(),
            angle: branch_angle,
            width: last_point.width,
            position: position + 1
          }
          ];
        }

        const corners: Corner[] = createCorners(points, selected_counter.corners, selected_counter.uuid);
        const new_counter: Counter = { ...counter, points, corners };
        dispatch(updateLocalCounter(new_counter));
        setSelectedCounter(new_counter);
      }
      else {
        return;
      }
    }
    else if (lifting && selected_counter !== null) {
      if (selected_counter) {
        const counter: Counter | null = moveCounter(selected_counter, getMouseAtZoom(lift_point, zoom), mouse);
        if (counter) {
          dispatch(updateLocalCounter(counter));
        }
      }
    }
    else if (selected_label && lifting_label) {
      const vector: Vector2 = moveVector(selected_label.location, getMouseAtZoom(mouse_data.position, zoom));
      dispatch(updateLocalLabel({ ...selected_label, location: vector }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mouse_data.position]);

  const deleteSelectedCounter = () => {
    setInOperation(true);
    if (selected_counter) {
      const counter_to_delete: Counter = { ...selected_counter };
      const uuid: string = counter_to_delete.uuid;
      const counter_id: number = counter_to_delete.id;
      handleDeleteCounter(
        dispatch,
        setSelectedCounter,
        uuid,
        counter_id,
        { ...selected_counter },
        cut_shapes
      );

    }
    setInOperation(false);
    setMenuOpen(false);
  }

  const rotate = (rotation: number) => {
    if (selected_counter) {
      handleRotateCounter(
        dispatch,
        setSelectedCounter,
        { ...selected_counter },
        rotation
      );
    }
  }

  const handleDuplicateCounter = () => {
    setInOperation(true);
    if (selected_counter) {
      const counter_copy: Counter = { ...selected_counter };
      const counter: Counter = duplicateCounter(counter_copy);
      const slab: Slab | null = getFirstSlabInAreas(areas, counter.area_uuid);
      handleCreateCounter(
        dispatch,
        quote.id,
        counter,
        counter.area_uuid,
        slab ? slab.uuid : areas[0].slabs[0].uuid
      );
    }
    setMenuOpen(false);
    setInOperation(false);
  }

  const handleChangeAreaOpen = () => {
    setAreaOpen(true);
    setMenuOpen(false);
  }

  const handleOpenEditLabel = () => {
    setEditLabelOpen(true);
    setLabelMenuOpen(false);
  }

  const duplicateLabel = () => {
    if (selected_label) {
      dispatch(createLabel({ text: selected_label.text, area: selected_label.area, size: selected_label.size, location: { X: 450, Y: 450 }, quote: quote.id }));
    }
    setLabelMenuOpen(false);
  }

  const handleDeleteLabel = () => {
    const id: number = selected_label.id;
    dispatch(deleteLabel(id));
    setLabelMenuOpen(false);
  }

  return (
    <div>
      <StoreAreaBar current_area={current_area} setArea={setCurrentArea} />
      <Box
        onContextMenu={(e) => {
          e.preventDefault();
        }}
        sx={sketch_style(copy ? "crosshair" : copying ? "grab" : "normal")}
        ref={ref}
        onMouseDown={(event: ReactMouseEvent) => handleEvent(event)}
        onMouseUp={(event: ReactMouseEvent) => handleEvent(event)} >
        <SketchMenu
          open={menu_open}
          menu_location={menu_location}
          changeArea={handleChangeAreaOpen}
          deleteCounter={deleteSelectedCounter}
          duplicateCounter={handleDuplicateCounter}
          rotate={rotate} />
        <LabelMenu
          open={label_menu_open}
          menu_location={menu_location}
          openEditLabel={handleOpenEditLabel}
          duplicateLabel={duplicateLabel}
          deleteLabel={handleDeleteLabel} />
        <canvas id="canvas" className={JSON.stringify(canvasStyle)} />
        <UpdateStoreHeight
          open={height_open}
          setOpen={setHeightOpen}
          original_value={original_value}
          point_uuid={current_uuid}
          selected_counter={selected_counter} />
        <UpdateStoreWidth
          open={width_open}
          setOpen={setWidthOpen}
          original_value={original_value}
          point_uuid={current_uuid}
          selected_counter={selected_counter} />
        <UpdateStoreDegrees
          open={degrees_open}
          setOpen={setDegreesOpen}
          original_value={original_value}
          point_uuid={current_uuid}
          selected_counter={selected_counter} />
        <UpdateStoreArea
          open={area_open}
          setOpen={setAreaOpen}
          counter={selected_counter} />
        <CreateStoreArea
          open={create_area_open}
          setOpen={setCreateAreaOpen} />
        <UpdateStoreLabel
          open={edit_label_open}
          setOpen={setEditLabelOpen}
          selected_label={selected_label} />
      </Box>
      {
        copying && copy ?
          <Stack sx={{ position: "absolute", top: mouse_data.position.Y + 30, left: mouse_data.position.X + 20, zIndex: 2000, backgroundColor: "#fff", border: "1px solid #666666", paddingLeft: "10px", paddingRight: "10px", paddingTop: "3px", paddingBottom: "3px" }}>
            <Typography variant="body2">
              {getCopyMessage(copy)}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 10 }} color="error">
              Right click to cancel
            </Typography>
          </Stack> :
          null
      }
    </div>
  )
}
